import { useMemo, useState } from 'react'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { useHaloUsers } from '@/features/halocrm-user/hooks'
import type { HaloUser } from '@/features/halocrm-user/types'
import { useUserRegions } from '@/features/region/hooks'
import { useRolesForUsers } from './hooks'
import { UserRolesDialog } from './components/UserRolesDialog'
import { UserRegionCell } from './components/UserRegionCell'

export function UsersAdminPage() {
  const [search, setSearch] = useState('')
  const [editingUser, setEditingUser] = useState<HaloUser | null>(null)
  const trimmed = search.trim()
  const usersQ = useHaloUsers({ active: false, search: trimmed, limit: 50 })

  const userIds = useMemo(
    () => (usersQ.data?.items ?? []).map(u => u.id),
    [usersQ.data],
  )
  const rolesQ = useRolesForUsers(userIds)
  const userRegionsQ = useUserRegions()
  const regionByUser = useMemo(() => {
    const map = new Map<number, string>()
    userRegionsQ.data?.forEach((ur) => map.set(ur.halocrmUserId, ur.regionCode))
    return map
  }, [userRegionsQ.data])

  return (
    <div className="flex flex-col w-full h-full">
      <ListPageHeader
        title="Użytkownicy"
        subtitle={
          <>
            Lista użytkowników z HaloCRM (auto-sync co 6h). Tu nadajesz im role - członkostwo w zespołach edytujesz w{' '}
            <a href="/admin/teams" className="text-sky-700 hover:underline">Zespołach</a>.
          </>
        }
      />

      <div className="p-4 flex flex-col gap-3 max-w-5xl">
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Wyszukaj po nazwisku lub e-mailu…"
          className="border rounded px-3 py-2 text-sm"
        />

        <div className="border rounded bg-white divide-y">
          {usersQ.isLoading && <div className="px-3 py-3 text-xs text-slate-500">Szukam…</div>}
          {usersQ.data && !trimmed && (
            <div className="px-3 py-3 text-xs text-slate-500">
              Wpisz fragment nazwiska lub maila, żeby zobaczyć użytkowników.
            </div>
          )}
          {usersQ.data && trimmed && usersQ.data.items.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">Brak wyników dla "{trimmed}"</div>
          )}
          {(usersQ.data?.items ?? []).map(u => {
            const userRoles = rolesQ.data?.[String(u.id)] ?? []
            return (
              <div key={u.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50">
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="font-medium truncate">{u.fullName || `user #${u.id}`}</span>
                  {u.email && <span className="text-[11px] text-slate-500 truncate">{u.email}</span>}
                </div>
                <div className="flex flex-wrap gap-1 max-w-sm justify-end">
                  {rolesQ.isLoading ? (
                    <span className="text-[10px] text-slate-300">…</span>
                  ) : userRoles.length === 0 ? (
                    <span className="text-[10px] text-slate-400 italic">brak ról</span>
                  ) : (
                    userRoles.map(code => (
                      <span
                        key={code}
                        className="text-[10px] font-mono bg-sky-50 text-sky-800 border border-sky-200 px-1.5 py-0.5 rounded"
                      >{code}</span>
                    ))
                  )}
                </div>
                <UserRegionCell userId={u.id} regionCode={regionByUser.get(u.id) ?? null} />
                {!u.isActive && (
                  <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded">nieaktywny</span>
                )}
                <button
                  type="button"
                  onClick={() => setEditingUser(u)}
                  className="text-xs bg-sky-600 text-white rounded px-2 py-1"
                >Role</button>
              </div>
            )
          })}
        </div>
      </div>

      {editingUser && (
        <UserRolesDialog user={editingUser} onClose={() => setEditingUser(null)} />
      )}
    </div>
  )
}
